import { useCallback, useMemo, useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  autoPlacement,
  arrow as arrowMiddleware,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  useTransitionStyles,
  type Placement,
  type UseFloatingOptions,
  safePolygon,
} from "@floating-ui/react";

export type FloatingPlacement = Placement | "auto";
export type FloatingDelay = number | [number, number];
export type FloatingTrigger = "hover" | "click" | "hover-click";

export interface UseFloatingOverlayOptions {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;

  placement?: FloatingPlacement;
  trigger?: FloatingTrigger;
  delay?: FloatingDelay;
  offset?: number;
  padding?: number;

  withArrow?: boolean;
  role: "tooltip" | "menu" | "dialog";

  /** Keep open while cursor moves between reference and floating content */
  interactive?: boolean;

  /**
   * Extra "forgiveness" border around the gap between reference and floating
   * when using hover (safe polygon). Similar to Tippy's interactiveBorder.
   */
  safeBorder?: number;
}

/**
 * Builds placement configuration with appropriate middleware.
 * Memoization-friendly: returns stable references when inputs don't change.
 */
export function getPlacementConfig(
  placement: FloatingPlacement = "bottom",
  offsetValue = 8,
  padding = 8,
): Pick<UseFloatingOptions, "placement" | "middleware"> {
  if (placement === "auto") {
    return {
      placement: "bottom" as Placement,
      middleware: [offset(offsetValue), autoPlacement({ padding })],
    };
  }

  return {
    placement,
    middleware: [offset(offsetValue), flip({ padding }), shift({ padding })],
  };
}

export function useFloatingOverlay(options: UseFloatingOverlayOptions) {
  const {
    open,
    defaultOpen = false,
    onOpenChange,
    placement = "bottom",
    trigger = "hover",
    delay = 0,
    offset: offsetValue = 8,
    padding = 8,
    withArrow = false,
    role: roleName,
    safeBorder = 12,
    interactive = false,
  } = options;

  const arrowRef = useRef<SVGSVGElement | null>(null);

  // Controlled vs uncontrolled state management
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [openDelay, closeDelay] = Array.isArray(delay) ? delay : [delay, 0];

  // Memoize middleware to prevent unnecessary recalculations
  const middleware = useMemo(() => {
    const baseMiddleware =
      placement === "auto"
        ? [offset(offsetValue), autoPlacement({ padding })]
        : [offset(offsetValue), flip({ padding }), shift({ padding })];

    // Arrow middleware should always be last and always include the ref
    // The middleware handles null refs gracefully
    if (withArrow) {
      return [...baseMiddleware, arrowMiddleware({ element: arrowRef })];
    }

    return baseMiddleware;
  }, [placement, offsetValue, padding, withArrow]);

  const floating = useFloating({
    open: isOpen,
    onOpenChange: setOpen,
    placement: placement === "auto" ? "bottom" : placement,
    middleware,
    whileElementsMounted: autoUpdate,
    // CRITICAL FIX: Disable transform-based positioning to avoid conflicts
    // with transition animations. This ensures floatingStyles uses top/left
    // instead of transform, preventing the transform override bug.
    strategy: "fixed", // Escapes parent stacking contexts like modals
    transform: false,
  });

  const { context } = floating;

  // Interaction hooks - always called unconditionally (Rules of Hooks)
  const click = useClick(context, {
    enabled: trigger === "click" || trigger === "hover-click",
  });

  const hover = useHover(context, {
    enabled: trigger === "hover" || trigger === "hover-click",
    delay: { open: openDelay, close: closeDelay },
    // move: false prevents reopening on mouse movement within the reference
    move: false,
    // Always use safePolygon for hover to prevent flickering when tooltip
    // renders near cursor position. This creates a "safe zone" between
    // the reference and floating element.
    handleClose: interactive
      ? safePolygon({
          buffer: safeBorder,
        })
      : undefined, // plain hover for non-interactive tooltips
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: roleName });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 100,
    initial: {
      opacity: 0,
      transform: "scale(0.95)",
    },
  });

  return {
    ...floating, // refs, floatingStyles, context, placement, etc.
    isOpen,
    setOpen,
    arrowRef,
    getReferenceProps,
    getFloatingProps,
    isMounted,
    transitionStyles,
  };
}
